import { createSlice } from '@reduxjs/toolkit'
import { FORMAT, formatDateTime } from '../../../../helpers/datetime'
import { register, syncInitialState } from '../../../../helpers/reducerRegistry'
import { SOURCE, TAB_LIST, TYPE_OF_FS } from '../constant'
import { convertData } from '../dynamicReportTable/helper'
import {
  handleAuditStatuses,
  handleFinancialStatementIds,
  handleFiscalYearChange,
  handleLegalRegulationData,
  handleMethod,
  handleNewGroupColumns,
  handleTabData,
  handleYearChangeFiscalYear,
  mappingDataApi,
} from '../helper'
import {
  financialStatementsOneYearTable,
  financialStatementsTable,
  getDataTransform,
  legalRegulations,
} from './thunk'

const initialState = {
  activeTab: TAB_LIST.BALANCE_SHEET.key,
  data: [],
  groupColumns: [],
  isLoading: false,
  balanceSheetIds: [],
  balanceSheetData: {},
  incomeStatementIds: [],
  incomeStatementData: {},
  cashFlowStatementIds: [],
  cashFlowStatementData: {},
  auditStatuses: [],
  methods: [],
  legalRegulations: [],
  fiscalYearEndChangeYear: undefined,
  isShowCommonSize: true,
  typeOfFS: undefined,
  source: undefined,
  savedOrgId: undefined,
  dataYears: [],
  yearChangeFiscalYear: undefined,
  legalRegulationData: [],
  indexRegulationData: 0,
  dataDynamicReport: {
    columns: [],
    balanceSheets: {},
    incomeStatements: {},
    cashFlows: {},
  },
}

const name = 'companyAnalysis/financial/financialStatements'
const persistKeys = [
  'activeTab',
  'auditStatuses',
  'methods',
  'legalRegulations',
  'isShowCommonSize',
  'typeOfFS',
  'source',
  'savedOrgId',
  'dataYears',
  'indexRegulationData',
]

const slice = createSlice({
  name,
  initialState: syncInitialState({ name, initialState, persistKeys }),
  reducers: {
    resetStoreCompanyFinancialStatement: () => initialState,
    changeBalanceSheetIds: (state, action) => {
      state.balanceSheetIds = action.payload
    },
    changeIncomeStatementIds: (state, action) => {
      state.incomeStatementIds = action.payload
    },
    changeCashFlowStatementIds: (state, action) => {
      state.cashFlowStatementIds = action.payload
    },
    changeSavedOrgId: (state, action) => {
      state.savedOrgId = action.payload
    },

    changeAuditStatus: (state, action) => {
      state.auditStatuses = action.payload
    },

    changeMethods: (state, action) => {
      state.methods = action.payload
    },

    changeData: (state, action) => {
      state.groupColumns = handleNewGroupColumns(
        action.payload,
        state.legalRegulations,
      )

      state.balanceSheetIds = handleFinancialStatementIds(
        action.payload,
        'balanceSheets',
      )
      state.balanceSheetData = handleTabData(
        action.payload,
        state.groupColumns,
        state.balanceSheetIds,
        'balanceSheets',
      )

      state.incomeStatementIds = handleFinancialStatementIds(
        action.payload,
        'incomeStatements',
      )
      state.incomeStatementData = handleTabData(
        action.payload,
        state.groupColumns,
        state.incomeStatementIds,
        'incomeStatements',
      )

      state.cashFlowStatementIds = handleFinancialStatementIds(
        action.payload,
        'cashFlows',
      )
      state.cashFlowStatementData = handleTabData(
        action.payload,
        state.groupColumns,
        state.cashFlowStatementIds,
        'cashFlows',
      )
    },
    changeActiveTab: (state, action) => {
      state.activeTab = action.payload
    },
    changeIsShowCommonSize: (state, action) => {
      state.isShowCommonSize = action.payload
    },
    changeTypeOfFS: (state, action) => {
      state.typeOfFS = action.payload
    },
    changeSource: (state, action) => {
      state.source = action.payload
    },
    changeInformPopup: (state, action) => {
      state.informPopup = { ...state.informPopup, ...action.payload }
    },
    changeIndexRegulationData: (state, action) => {
      state.indexRegulationData = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(financialStatementsTable.pending, (state) => {
      state.isLoading = true
    })
    builder.addCase(financialStatementsTable.fulfilled, (state, action) => {
      state.isLoading = false
      const data = mappingDataApi(action.payload)
      state.data = data
      state.dataYears = data.length ? data.map((item) => item.realYear) : []
      state.typeOfFS = data?.length
        ? data?.[data.length - 1].isConsolidate
          ? TYPE_OF_FS.consolidated
          : TYPE_OF_FS.unconsolidated
        : state.typeOfFS || TYPE_OF_FS.consolidated
      state.source = data?.length
        ? Object.values(SOURCE).find(
            (item) => item.value === data?.[0].sourceOrganizationId,
          )
        : state.source || SOURCE.publicAnnouncement

      state.auditStatuses = handleAuditStatuses(data)

      state.methods = handleMethod(data)

      state.legalRegulationData = handleLegalRegulationData(data)

      if (!state.legalRegulationData[state.indexRegulationData]) {
        state.indexRegulationData = 0
      }

      const legalRegulationData =
        state.legalRegulationData[state.indexRegulationData]?.data || []

      const yearWithFiscalYear = [...data].reverse().map((item) => {
        return {
          year: item.realYear,
          fiscalYearEnd: formatDateTime(item.fiscalYearEnd, FORMAT.DAY_MONTH),
        }
      })

      state.yearChangeFiscalYear =
        handleYearChangeFiscalYear(yearWithFiscalYear)
      state.fiscalYearEndChangeYear = handleFiscalYearChange(
        [...data].reverse(),
      )

      state.groupColumns = data.length
        ? handleNewGroupColumns(legalRegulationData, state.legalRegulations)
        : []

      state.balanceSheetIds = handleFinancialStatementIds(
        legalRegulationData,
        'balanceSheets',
      )
      state.balanceSheetData = handleTabData(
        legalRegulationData,
        state.groupColumns,
        state.balanceSheetIds,
        'balanceSheets',
      )

      state.incomeStatementIds = handleFinancialStatementIds(
        legalRegulationData,
        'incomeStatements',
      )
      state.incomeStatementData = handleTabData(
        legalRegulationData,
        state.groupColumns,
        state.incomeStatementIds,
        'incomeStatements',
      )

      state.cashFlowStatementIds = handleFinancialStatementIds(
        legalRegulationData,
        'cashFlows',
      )

      state.cashFlowStatementData = handleTabData(
        legalRegulationData,
        state.groupColumns,
        state.cashFlowStatementIds,
        'cashFlows',
      )
    })
    builder.addCase(financialStatementsTable.rejected, (state) => {
      state.isLoading = false
    })

    builder.addCase(
      financialStatementsOneYearTable.fulfilled,
      (state, action) => {
        state.isLoading = false
        const data = mappingDataApi(action.payload)
        state.data = data

        state.auditStatuses = handleAuditStatuses(data)

        state.methods = handleMethod(data)

        state.legalRegulationData = handleLegalRegulationData(data)

        if (!state.legalRegulationData[state.indexRegulationData]) {
          state.indexRegulationData = 0
        }

        const legalRegulationData =
          state.legalRegulationData[state.indexRegulationData]?.data || []

        state.groupColumns = handleNewGroupColumns(
          legalRegulationData,
          state.legalRegulations,
        )

        state.balanceSheetIds = handleFinancialStatementIds(
          legalRegulationData,
          'balanceSheets',
        )

        state.balanceSheetData = handleTabData(
          legalRegulationData,
          state.groupColumns,
          state.balanceSheetIds,
          'balanceSheets',
        )

        state.incomeStatementIds = handleFinancialStatementIds(
          legalRegulationData,
          'incomeStatements',
        )

        state.incomeStatementData = handleTabData(
          legalRegulationData,
          state.groupColumns,
          state.incomeStatementIds,
          'incomeStatements',
        )

        state.cashFlowStatementIds = handleFinancialStatementIds(
          legalRegulationData,
          'cashFlows',
        )

        state.cashFlowStatementData = handleTabData(
          legalRegulationData,
          state.groupColumns,
          state.cashFlowStatementIds,
          'cashFlows',
        )
      },
    )
    builder.addCase(legalRegulations.fulfilled, (state, action) => {
      state.legalRegulations = action.payload
    })
    builder.addCase(getDataTransform.fulfilled, (state, action) => {
      state.dataDynamicReport = convertData(action.payload)
    })
  },
})

// export Regulations
export const selectLegalRegulations = (state) => {
  return state[slice.name].legalRegulations
}

export const selectLegalRegulationData = (state) =>
  state[slice.name].legalRegulationData

export const selectIndexRegulationData = (state) =>
  state[slice.name].indexRegulationData

// export balanceSheet
export const selectBalanceSheetIds = (state) =>
  state[slice.name].balanceSheetIds

export const selectBalanceSheetData = (state) => {
  return state[slice.name].balanceSheetData
}

export const selectBalanceSheetItem = (rowId, colId) => (state) => {
  return state[slice.name].balanceSheetData?.[rowId]?.[colId]
}

export const selectBalanceSheetRowData = (rowId) => (state) => {
  return state[slice.name].balanceSheetData[rowId]
}

export const selectBalanceSheetChildIds = (rowId) => (state) => {
  return state[slice.name].balanceSheetData[rowId]?.childrenIds
}

export const selectBalanceSheetParentId = (rowId) => (state) => {
  return state[slice.name].balanceSheetData[rowId]?.parentId
}

// export Income Statement
export const selectIncomeStatementIds = (state) =>
  state[slice.name].incomeStatementIds

export const selectIncomeStatementData = (state) => {
  return state[slice.name].incomeStatementData
}

export const selectIncomeStatementItem = (rowId, colId) => (state) => {
  return state[slice.name].incomeStatementData?.[rowId]?.[colId]
}

export const selectIncomeStatementRowData = (rowId) => (state) => {
  return state[slice.name].incomeStatementData[rowId]
}

export const selectIncomeStatementChildIds = (rowId) => (state) => {
  return state[slice.name].incomeStatementData[rowId]?.childrenIds
}

export const selectIncomeStatementParentId = (rowId) => (state) => {
  return state[slice.name].incomeStatementData[rowId]?.parentId
}

// export Cash Flow
export const selectCashFlowStatementIds = (state) =>
  state[slice.name].cashFlowStatementIds

export const selectCashFlowStatementData = (state) => {
  return state[slice.name].cashFlowStatementData
}

export const selectCashFlowStatementItem = (rowId, colId) => (state) => {
  return state[slice.name].cashFlowStatementData?.[rowId]?.[colId]
}

export const selectCashFlowStatementDataRowData = (rowId) => (state) => {
  return state[slice.name].cashFlowStatementData[rowId]
}

export const selectCashFlowStatementChildIds = (rowId) => (state) => {
  return state[slice.name].cashFlowStatementData[rowId]?.childrenIds
}

export const selectCashFlowStatementParentId = (rowId) => (state) => {
  return state[slice.name].cashFlowStatementData[rowId]?.parentId
}

// export common

export const selectGroupColumns = (state) => {
  return state[slice.name].groupColumns
}

export const selectSavedOrgId = (state) => {
  return state[slice.name].savedOrgId
}

export const selectAuditStatuses = (state) => {
  return state[slice.name].auditStatuses
}

export const selectMethods = (state) => {
  return state[slice.name].methods
}

export const selectIsLoading = (state) => {
  return state[slice.name].isLoading
}

export const selectFiscalYearEndChangeYear = (state) =>
  state[slice.name].fiscalYearEndChangeYear

export const selectIsShowCommonSize = (state) =>
  state[slice.name].isShowCommonSize

export const selectTypeOfFS = (state) => state[slice.name].typeOfFS

export const selectSource = (state) => state[slice.name].source

export const selectActiveTab = (state) => state[slice.name].activeTab

export const selectInformPopup = (state) => state[slice.name].informPopup

export const selectDataYears = (state) => state[slice.name].dataYears
export const data = (state) => state[slice.name].data

// Notification part
export const selectYearChangeFiscalYear = (state) =>
  state[slice.name].yearChangeFiscalYear

export const selectDataDynamicReport = (state) =>
  state[slice.name].dataDynamicReport

export const selectStateFinancialStatement = (state) => state[slice.name]

export const {
  changeBalanceSheetIds,
  changeIncomeStatementIds,
  changeCashFlowStatementIds,
  changeData,
  changeTypeOfFS,
  changeIsShowCommonSize,
  changeSource,
  changeInformPopup,
  changeAuditStatus,
  changeMethods,
  changeActiveTab,
  changeSavedOrgId,
  resetStoreCompanyFinancialStatement,
  changeIndexRegulationData,
} = slice.actions

register(slice.name, slice.reducer, persistKeys)
